import React, { type FC } from "react";
import { useDivisionFilterSeed } from "@app/session-defaults/useDivisionFilterSeed";
import { useUserStore } from "@app/store/useUserStore";
import { DivisionType } from "@src/api/dictionaries/divisions";
import { BusinessForm } from "@src/components/business-form";
import { DivisionSearchSelect, DivisionSectionSearchSelect } from "@src/components/business-data-entry";
import { Checkbox } from "@shared/ui/checkbox";
import { FilterFormPanel } from "@shared/ui/form-panel";
import type { StockKanbanContainerFilter } from "../../../../../../api/stockKanbanContainers";
import type { ListFilterPanelProps } from "../../../../../../shared/listFilterPanel";
import {
    stockKanbanSupplyFilterFormConfig,
    toStockKanbanSupplyFilter,
    type StockKanbanSupplyFilterFormValues,
} from "./stockKanbanSupplyFilter.form-config";

/** Пропсы компонента {@link StockKanbanSupplyFilterForm}. */
export type StockKanbanSupplyFilterFormProps = ListFilterPanelProps<StockKanbanContainerFilter>;

/** Форма фильтра контейнеров КАНБАН. */
export const StockKanbanSupplyFilterForm: FC<StockKanbanSupplyFilterFormProps> = ({ initialFilter, onApply }) => {
    const currentUserCode = useUserStore((state) => state.currentUser?.Code ?? null);
    /** Типы подразделений-потребителей (цех / кладовая); коды из bootstrap `DivisionType`. */
    const consumerDivisionTypes = [DivisionType.Цех, DivisionType.Кладовая];
    const seed = useDivisionFilterSeed<StockKanbanSupplyFilterFormValues>("divisionCode", {
        filter: { Types: consumerDivisionTypes },
    });

    return (
        <FilterFormPanel
            initialFilter={initialFilter}
            seed={seed}
            submissionMemory={{
                key: "stock.kanbanSupply.filter",
                schemaVersion: 3,
                fields: ["divisionCode", "sectionCode", "onlyCurrentUser"],
            }}
            formProps={{ formConfig: stockKanbanSupplyFilterFormConfig }}
            toForm={(filter) => stockKanbanSupplyFilterFormConfig.toForm(filter)}
            toDomain={(values) => toStockKanbanSupplyFilter(values, currentUserCode)}
            onApply={onApply}
        >
            {(form) => {
                const divisionCode = form.watch("divisionCode");

                return (
                    <>
                        <BusinessForm.Field name="divisionCode">
                            <DivisionSearchSelect
                                {...form.field("divisionCode").getSearchSelectProps((division) => division?.Code ?? null)}
                                onChange={(division) => {
                                    // TODO(forms/spp-lib): cascade-clear via form-config `dependsOn` (multi-field masters)
                                    const nextDivisionCode = division?.Code ?? null;

                                    form.setValue("divisionCode", nextDivisionCode);
                                    if (nextDivisionCode !== divisionCode) {
                                        form.setValue("sectionCode", null);
                                    }
                                }}
                                flt={{ Types: consumerDivisionTypes }}
                                fullscreenTitle="Выберите подразделение-потребитель"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="sectionCode">
                            <DivisionSectionSearchSelect
                                {...form
                                    .field("sectionCode")
                                    .getSearchSelectProps((section) => section?.SectionCode ?? null)}
                                divisionCode={divisionCode}
                                fullscreenTitle="Выберите участок-потребитель"
                            />
                        </BusinessForm.Field>
                        <BusinessForm.Field name="onlyCurrentUser">
                            <Checkbox {...form.field("onlyCurrentUser").getCheckboxProps()} />
                        </BusinessForm.Field>
                    </>
                );
            }}
        </FilterFormPanel>
    );
};
